/**
 * Copyright (c) 2024 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : core.jsx
 * description   : {{insert here}}
 * creation date : 14/01/2024
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';

import {
    useNavigate,
    useLocation,
} from 'react-router-dom';

import {
    osName,
    osVersion,
    browserName,
    browserVersion,
    deviceType,
} from 'react-device-detect';

import {
    Auth,
} from 'aws-amplify';

import {
    useAuthenticator,
    useTheme,
} from '@aws-amplify/ui-react';

import {
    search_list,
    route_list,
    auth_routes,
} from '@/utils/router/core';

import {
    toolKit,
} from '@/components/toolKit';



// --------------------------------------------------------------------------------
//      CONSTANTS DEFINITION
// --------------------------------------------------------------------------------

const deviceProperties =
{
    type: deviceType,
    os: {
        name: osName,
        version: osVersion,
    },
    browser: {
        name: browserName,
        version: browserVersion,
    },
};



// --------------------------------------------------------------------------------
//      CONTEXT ARTIFACTS
// --------------------------------------------------------------------------------

const ComponentWrapperContext = createContext();

function useCtx() {
    // Amplify theme
    const { tokens } = useTheme();

    // State variables
    const [ctx_screenProperties, set_ctx_screenProperties] = useState(toolKit.api.window.getScreenProperties());
    const [ctx_authState, set_ctx_authState] = useState(toolKit.enums.auth.stateTypes.UNMOUNTED);
    const [ctx_authUser, set_ctx_authUser] = useState(null);
    const [ctx_isLoaded, set_ctx_IsLoaded] = useState(false);
    const [ctx_config, /* set_ctx_config */] = useState(
        () => {
            // Set the runtime configuration
            if (!process.env.NODE_ENV || (process.env.NODE_ENV === 'development')) {
                return toolKit.enums.runtime.configTypes.DEVELOPMENT;
            } else {
                return toolKit.enums.runtime.configTypes.PRODUCTION;
            }
        }
    );

    // Fetch the authenticator user properties
    const { authStatus, route, user } = useAuthenticator();

    // Current location
    const location = useLocation();

    // For navigation purposes
    const navigate = useNavigate();

    // Listen asynchronous events: Authentication state
    useEffect(() => {
        // Detect sign in event
        if (
            (authStatus === 'authenticated') &&
            (route === 'authenticated') &&
            (user) &&
            (ctx_authState !== toolKit.enums.auth.stateTypes.AUTHENTICATED)
        ) {
            set_ctx_authState(toolKit.enums.auth.stateTypes.AUTHENTICATED);
            set_ctx_authUser(user);
        }

        // Detect sign out event
        if (
            (
                (authStatus === 'unauthenticated') &&
                (route === 'setup') &&
                (!user) &&
                (ctx_authState === toolKit.enums.auth.stateTypes.UNMOUNTED)
            ) ||
            (
                (authStatus === 'authenticated') &&
                (route === 'signOut') &&
                (user) &&
                (ctx_authState === toolKit.enums.auth.stateTypes.AUTHENTICATED)
            )
        ) {
            set_ctx_authState(toolKit.enums.auth.stateTypes.UNAUTHENTICATED);
            set_ctx_authUser(null);
        }
    },
        [
            authStatus,
            route,
            user,
            ctx_authState,
        ]
    );

    // Listen asynchronous events: Screen resize
    useEffect(() => {
        function handleResize() {
            set_ctx_screenProperties(toolKit.api.window.getScreenProperties());
        }

        window.addEventListener('resize', handleResize);

        return () =>
            window.removeEventListener('resize', handleResize);
    },
        [
        ]
    );

    // Listen asynchronous events: Page load
    useEffect(() => {
        function onPageLoad() {
            if (!ctx_isLoaded) {
                set_ctx_IsLoaded(true);
            }
        };

        if (document.readyState === 'complete') {
            onPageLoad();
        } else {
            // Remove the event listener
            window.addEventListener('load', onPageLoad, false);

            // Remove the event listener when component unmounts
            return () =>
                window.removeEventListener('load', onPageLoad);
        }
    },
        [
            ctx_isLoaded,
        ]
    );

    // Exported functions
    function ctx_api_location_getLocation() {
        return {
            path: ctx_api_location_getPath(),
            search: ctx_api_location_getSearch(),
            hash: ctx_api_location_getHash()
        };
    }

    function ctx_api_location_getPath() {
        return location.pathname;
    }

    function ctx_api_location_getSearch() {
        const params = new URLSearchParams(location.search);
        const result = {};

        // Populate the dictionary
        for (const [key, value] of params.entries()) {
            result[key] = value;
        }

        return result;
    }

    function ctx_api_location_getHash() {
        var arr = location.hash.split('#');
        const result = [];

        // Remove the invalid items
        arr = arr.filter((a) => a);

        // Decode the content
        for (const idx in arr) {
            result.push(decodeURIComponent(arr[idx]));
        }

        return result;
    }

    function ctx_api_utils_getCurUrl() {
        const result = toolKit.api.location.buildUrl(
            ctx_api_location_getPath(),
            ctx_api_location_getSearch(),
            ctx_api_location_getHash()
        );

        return result;
    }

    function ctx_api_utils_buildUrlAuth(
        route,
        returnTo = false
    ) {
        // Computed AUTH URL
        var result;

        // Assert the target route
        if (auth_routes.list.indexOf(route) <= -1) {
            console.error('Route "%s" unknown', route);

            // Force 'SIGN_IN' path
            route = auth_routes.SIGN_IN;
        }

        // Compute the path
        const path = route_list.AUTH + '/' + route;

        // Get the current location path
        const cur_path = ctx_api_location_getPath();

        // If already in a AUTH page
        if (cur_path.startsWith(route_list.AUTH + '/')) {
            // Get the current authentication route
            const cur_auth_route = cur_path.replace(route_list.AUTH + '/', '');

            // If it is the same AUTH page, prevent losing search and hash
            if (route === cur_auth_route) {
                result = ctx_api_utils_getCurUrl();
            }
            else {
                result = toolKit.api.location.buildUrl(
                    path,
                    ctx_api_location_getSearch(),
                    ctx_api_location_getHash()
                );
            }

            return result;
        }

        // Search dictionary
        const search = {};

        // Process the returnTo URL, if necessary
        if (returnTo) {
            search[search_list.RETURN_TO] = ctx_api_utils_getCurUrl();
        }

        // Build the URL
        result = toolKit.api.location.buildUrl(
            path,
            search
        );

        return result;
    }

    function ctx_api_utils_adaptiveOption(optionLarge, optionMedium, optionSmall) {
        const result = toolKit.api.utils.adaptiveOption(
            ctx_screenProperties.viewPort,
            optionLarge,
            optionMedium,
            optionSmall,
        )

        return result;
    }

    function ctx_api_location_navigate(
        target_path,
        target_search = {},
        target_hash = [],
        force = false
    ) {
        if (
            (force) ||
            (['', ctx_api_location_getPath()].indexOf(target_path) <= -1)
        ) {
            const url = toolKit.api.location.buildUrl(target_path, target_search, target_hash);

            navigate(url);
        }
    }

    function ctx_api_location_navigateToAuth(
        route,
        returnTo = false
    ) {
        ctx_api_location_navigate(
            ctx_api_utils_buildUrlAuth(route, returnTo)
        );
    }

    function ctx_api_location_canBeDisplayed(page) {
        const result = toolKit.api.location.canBeDisplayed(page, ctx_authState);

        return result;
    }

    async function ctx_api_auth_signIn(credentials) {
        var result = toolKit.enums.auth.procStatusTypes.UNKNOWN;

        if (ctx_authState === toolKit.enums.auth.stateTypes.UNAUTHENTICATED) {
            try {
                await Auth.signIn(credentials);

                result = toolKit.enums.auth.procStatusTypes.SUCCESS;
            }
            catch (error) {
                result = toolKit.enums.auth.procStatusTypes.ERROR_BAD_CREDENTIALS;
            }
        }
        else {
            result = toolKit.enums.auth.procStatusTypes.WARN_ALREADY_AUTHENTICATED;
        }

        return result;
    }

    async function ctx_api_auth_signOut() {
        var result = toolKit.enums.auth.procStatusTypes.UNKNOWN;

        if (ctx_authState === toolKit.enums.auth.stateTypes.AUTHENTICATED) {
            try {
                await Auth.signOut();

                result = toolKit.enums.auth.procStatusTypes.SUCCESS;
            }
            catch (error) {
                result = toolKit.enums.auth.procStatusTypes.ERROR_SIGN_OUT;
            }
        }
        else {
            result = toolKit.enums.auth.procStatusTypes.WARN_ALREADY_AUTHENTICATED;
        }

        return result;
    }

    // Exported context
    return {
        ctx:
        {
            runtime:
            {
                device: deviceProperties,
                screen: ctx_screenProperties,
                isLoaded: ctx_isLoaded,
                config: ctx_config,
            },
            auth:
            {
                state: ctx_authState,
                user: ctx_authUser,
            },
            api:
            {
                auth:
                {
                    signIn: ctx_api_auth_signIn,
                    signOut: ctx_api_auth_signOut,
                },
                location:
                {
                    getLocation: ctx_api_location_getLocation,
                    getPath: ctx_api_location_getPath,
                    getSearch: ctx_api_location_getSearch,
                    getHash: ctx_api_location_getHash,
                    navigate: ctx_api_location_navigate,
                    navigateToAuth: ctx_api_location_navigateToAuth,
                    canBeDisplayed: ctx_api_location_canBeDisplayed,
                },
                utils:
                {
                    buildUrlAuth: ctx_api_utils_buildUrlAuth,
                    getCurUrl: ctx_api_utils_getCurUrl,
                    adaptiveOption: ctx_api_utils_adaptiveOption,
                },
            },
            theme:
            {
                tokens: tokens,
            },
        }
    };
}

function ComponentContext({ children }) {
    const ctx = useCtx();

    return (
        <ComponentWrapperContext.Provider value={ctx}>
            {children}
        </ComponentWrapperContext.Provider>
    );
}

function useComponentContext() {
    return useContext(ComponentWrapperContext);
}



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export {
    ComponentContext,
    useComponentContext,
    toolKit,
};



// End of file
